import type {
  WhatsAppContextField,
  WhatsAppTemplateVariableMapping,
} from '../../models/MasterAdmin/whatsappHub';

function cleanText(value: unknown): string {
  const text = String(value ?? '').trim();
  return /^(null|undefined)$/i.test(text) ? '' : text;
}

function sanitizeMapping(
  mapping: WhatsAppTemplateVariableMapping,
  slot: number
): WhatsAppTemplateVariableMapping {
  const sourceType =
    mapping.sourceType === 'static' || mapping.sourceType === 'deep_link'
      ? mapping.sourceType
      : 'context';
  const contextKey = cleanText(mapping.contextKey);
  const label = cleanText(mapping.label) || contextKey || `Variable ${slot}`;
  const staticValue = sourceType === 'static' ? cleanText(mapping.staticValue) : '';
  const deepLinkCampaignId =
    sourceType === 'deep_link' ? cleanText(mapping.deepLinkCampaignId) || null : null;

  return {
    ...mapping,
    slot,
    sourceType,
    label,
    contextKey: sourceType === 'context' ? contextKey || '' : null,
    staticValue,
    deepLinkCampaignId,
    deepLinkUrlType: sourceType === 'deep_link' ? mapping.deepLinkUrlType || 'short' : undefined,
    deepLinkParamBindings: { ...(mapping.deepLinkParamBindings || {}) },
  };
}

export function buildVariableMappings(
  count: number,
  existing: WhatsAppTemplateVariableMapping[] | undefined,
  fields: WhatsAppContextField[]
): WhatsAppTemplateVariableMapping[] {
  const prior = existing || [];
  const maxMappedSlot = prior.reduce((max, m) => Math.max(max, Number(m.slot) || 0), 0);
  const effectiveCount = Math.max(count || 0, prior.length, maxMappedSlot);
  if (effectiveCount <= 0) return [];
  return Array.from({ length: effectiveCount }, (_, i) => {
    const slot = i + 1;
    const found = prior.find((m) => m.slot === slot);
    if (found) {
      const sanitized = sanitizeMapping(found, slot);
      if (sanitized.sourceType === 'context' && sanitized.contextKey) {
        const field = fields.find((f) => f.key === sanitized.contextKey);
        if (field) {
          return { ...sanitized, label: field.label, contextKey: field.key };
        }
      }
      return sanitized;
    }
    const field = fields[i];
    return {
      slot,
      label: field?.label || `Variable ${slot}`,
      contextKey: field?.key || '',
      sourceType: 'context',
      deepLinkCampaignId: null,
      deepLinkUrlType: 'short',
      deepLinkParamBindings: {},
    };
  });
}

export function resolveMappingSource(
  eventMappings: WhatsAppTemplateVariableMapping[] | undefined,
  templateMappings: WhatsAppTemplateVariableMapping[] | undefined
): WhatsAppTemplateVariableMapping[] | undefined {
  const eventFilled = (eventMappings || []).filter((m) => {
    if (m.sourceType === 'static') return Boolean(cleanText(m.staticValue));
    if (m.sourceType === 'deep_link') return Boolean(cleanText(m.deepLinkCampaignId));
    return Boolean(cleanText(m.contextKey));
  });
  if (eventFilled.length) {
    return eventMappings!.map((m, idx) => sanitizeMapping(m, Number(m.slot) || idx + 1));
  }
  if (templateMappings?.length) {
    return templateMappings.map((m, idx) => sanitizeMapping(m, Number(m.slot) || idx + 1));
  }
  // Keep empty event overrides only when explicitly saved with slots but no sources yet
  if (eventMappings?.length) {
    return eventMappings.map((m, idx) => sanitizeMapping(m, Number(m.slot) || idx + 1));
  }
  return undefined;
}
